import { combine, serializeFormUrlencoded } from '@inwink/utils/url';
import { IFetchResponse } from '@inwink/apibase/isomorphic-fetch';
import { IAPIProxy } from '@inwink/apibase';

interface IRequestInfo {
    baseurl?: string;
    path: string;
    method: string;
    data?: any;
    options?: any;
    eventid?: string;
}

export default function getCookieAPIProxy(apiUrl: string): IAPIProxy {
    const _defaultTimeout: number = null;

    function _merge(request: IRequestInfo, apiOptions: Record<string, any>): string {
        let url = combine(request.baseurl, request.eventid, request.path);
        if (request.method === 'GET') {
            if (request.data) {
                const data = serializeFormUrlencoded(request.data);
                if (data) {
                    url += (url.indexOf('?') < 0 ? '?' : '&') + data;
                }
            }
        } else if (['POST', 'DELETE', 'PATCH'].indexOf(request.method) > -1) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            apiOptions["body"] = request.data;
        }
        return url;
    }

    function _send(request: IRequestInfo, retry = false): Promise<any> {
        // eslint-disable-next-line max-len
        const apiOptions = Object.assign({}, request?.options ?? {}, { method: request.method, headers: request?.options?.headers ?? {} });
        const url = _merge(request, apiOptions);
        const defaultFetchOptions: RequestInit = {
            credentials: 'include'
        };

        // if (url.indexOf("https://") == 0) {
        //     defaultFetchOptions = _defaultFetchOptions && _defaultFetchOptions.https;
        // } else {
        //     defaultFetchOptions = _defaultFetchOptions && _defaultFetchOptions.http;
        // }
        const fetchOptions = Object.assign({}, apiOptions, defaultFetchOptions);
        return new Promise((resolveSend, rejectSend) => {
            let timedOut = false;
            let timeoutRef = null;
            const timeout = (request.options && request.options.timeout) || _defaultTimeout;
            if (timeout) {
                timeoutRef = setTimeout(() => {
                    timedOut = true;
                    timeoutRef = null;
                    rejectSend({ message: "request timeout (" + timeout + "ms) " + url });
                }, timeout);
            }

            return fetch(url, fetchOptions).then((response) => {
                if (timedOut) {
                    return null;
                }

                if (timeoutRef) {
                    clearTimeout(timeoutRef);
                }
                // iOS n'a pas d'objet status, cf. : https://developer.mozilla.org/en-US/docs/Web/API/Response
                // eslint-disable-next-line max-len
                const httpStatus = (response && response.status) || (response && (response as any).result && (response as any).result.status);

                if (httpStatus === 401) {
                    if (retry) {
                        // onunauthenticated();
                        throw new Error("Unauthorized response from server : " + response.statusText + " while calling " + url);
                    }
                    // return _refreshToken(request, httpStatus);
                }

                if (httpStatus === 403) {
                    // return unauthorizedHandler(response, request).then((unauthorizedHandlerRes) => {
                    //     if (unauthorizedHandlerRes.continueProcess) {
                    //         if (retry) {
                    //             onunauthorized();
                    //             throw new Error("Forbidden response from server : " + response.statusText + " while calling " + url);
                    //         }

                    //         return _refreshToken(request, httpStatus);
                    //     } else if (unauthorizedHandlerRes.overrideResponse) {
                    //         return unauthorizedHandlerRes.responseValue;
                    //     }
                    // });
                }

                if (httpStatus >= 400) {
                    // logger.warn("Error " + response.status + " while call (" + fetchOptions.method + " " + url + ") response : " + response.statusText + " with body (" + (fetchOptions.body || "") + ")");
                    return Promise.reject(response);
                }

                return response;
            }, (err) => {
                if (timedOut) {
                    return null;
                }
                if (timeoutRef) {
                    clearTimeout(timeoutRef);
                }

                if (err && err.code === 17) {

                    if (retry) {
                        // onunauthorized();
                        throw new Error("La loose de Edge....");
                    }
                    // return _refreshToken(request, 17);



                } else {
                    // logger.error("unhandled fetch error", err);
                    return Promise.reject(err);
                }
            }).then((res) => {
                if (!timedOut) {
                    resolveSend(res);
                }
            }, (err) => {
                if (!timedOut) {
                    rejectSend(err);
                }
            });
        });
    }

    const get = (url: string, data?, options?) => {
        return _send({
            baseurl: apiUrl,
            path: url,
            method: 'GET',
            options: options,
            data: data
        });
    };

    const patch = (url: string, data?, options?) => {
        return _send({
            baseurl: apiUrl,
            path: url,
            method: 'PATCH',
            options: options,
            data: data
        });
    };

    const post = (url: string, data?, options?) => {
        return _send({
            baseurl: apiUrl,
            path: url,
            method: 'POST',
            options: options,
            data: data
        });
    };

    const processJsonResponse = (response: IFetchResponse) => {
        if (response.status == 200) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                return response.text().then(reponseText =>
                    JSON.parse(reponseText.replace(/(\u2028|\u2029|\u008D)/g, ""))
                );
            }
        }
        return response;
    };

    return {
        baseUrl: apiUrl,
        get: get,
        getJson: (url: string, data?, options?) => get(url, data, options).then(processJsonResponse),
        post: post,
        postJson: (url: string, data?, options?) => post(url, data, options).then(processJsonResponse),
        patch: patch,
        patchJson: (url: string, data?, options?) => patch(url, data, options).then(processJsonResponse),
    };
}